import FsLightbox from 'fslightbox-react';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useData } from '../hooks/useData';
import { useHeading } from '../hooks/useHeading';
import Controls from './Controls';
import Features from './Features';
import Heading from './Heading';
import Button from './ui/Button';
import Link from './ui/Link';

export default function App(props) {
	const getLabels = () => data?.labels || {};
	const getLabel = (key) => getLabels()[key] || '';

	const rootRef = useRef(null);
	const { data, loading, error } = useData(props.restUrl);
	const [categoryFilterTerm, setCategoryFilterTerm] = useState('all');
	const [searchValue, setSearchValue] = useState('');
	const [view, setView] = useState(props.view);
	const [lightboxImages, setLightboxImages] = useState([{ url: '', title: '' }]);
	const [lightboxKey, setLightboxKey] = useState(0);
	const [toggler, setToggler] = useState(false);
	const { headingTitle, headingDescription } = useHeading({
		data,
		customTitle: props.customTitle,
		customDescription: props.customDescription,
		searchValue,
		categoryFilterTerm,
		getLabel,
	});

	// Set the selection to 'staff_pick' if it exists in the categories
	useEffect(() => {
		if (data && !data.isFiltered && 'staff_pick' in data.categories) {
			setCategoryFilterTerm('staff_pick');
		}
	}, [data]);

	const handleViewOptionChange = () => {
		setView((view) => {
			return view === 'grid' ? 'list' : 'grid';
		});
	};

	const getAllFeaturesButton = () => {
		if (props.hideAllFeaturesButton) {
			return null;
		}

		if (data.isFiltered) {
			return (
				<div className="feature__show-all">
					<Link classes={['btn btn--center']} href={data.featurePageUrl}>
						{getLabel('show_me_all')}
					</Link>
				</div>
			);
		} else if (categoryFilterTerm !== 'all') {
			return (
				<div className="feature__show-all">
					<Button onClick={showAllFeatures} center={true}>
						{getLabel('show_me_all')}
					</Button>
				</div>
			);
		}
	};

	const showAllFeatures = () => {
		setCategoryFilterTerm('all');
		rootRef.current.scrollIntoView({ behavior: 'smooth' });
	};

	const showLightbox = (key, images) => {
		setLightboxImages(images);
		setLightboxKey(key);
		setToggler(!toggler);
	};

	useEffect(() => {
		setToggler(!toggler);
	}, [lightboxImages]);

	const lightboxPortal = () => {
		return createPortal(
			<FsLightbox
				key={lightboxKey}
				toggler={toggler}
				sources={lightboxImages.map((image) => image['url'])}
				captions={lightboxImages.map((image) => image['title'])}
			/>,
			document.getElementById('lightbox-wrapper'),
		);
	};

	if (!loading && !error) {
		return (
			<div ref={rootRef}>
				<Heading type="h2" title={headingTitle} description={headingDescription} />
				<Controls
					categories={data.categories}
					clearSearchField={() => setSearchValue('')}
					setSearchValue={setSearchValue}
					setCategoryFilterTerm={setCategoryFilterTerm}
					handleViewChange={handleViewOptionChange}
					getLabel={getLabel}
					searchValue={searchValue}
					categoryFilterTerm={categoryFilterTerm}
					view={view}
					isFiltered={data.isFiltered}
				/>
				<Features
					categories={data.categories}
					categoryFilterTerm={categoryFilterTerm}
					editions={data.editions}
					features={data.features}
					getLabel={getLabel}
					searchValue={searchValue}
					showLightbox={showLightbox}
					useListView={view === 'list'}
					isFiltered={data.isFiltered}
				/>
				{getAllFeaturesButton()}
				{lightboxPortal()}
			</div>
		);
	}
}

App.propTypes = {
	customDescription: PropTypes.string,
	customTitle: PropTypes.string,
	hideAllFeaturesButton: PropTypes.bool,
	restUrl: PropTypes.string,
	view: PropTypes.string,
};
